// components/SelfPaced/CurriculumBuilder.js
// Admin component for managing modules and lessons within a self-paced course.
// Composed from ModuleCard, AddModuleForm, and AddLessonModal sub-components.
import React, { useState, useCallback, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { Plus, Layers, Lock } from 'lucide-react';
import {
  createModule, updateModule, deleteModule,
  deleteLesson, confirmLessonUpload,
  updateSelfPacedCourse,
  triggerAIExplanation, retryAIExplanation, getBulkAIStatus,
} from '../../utils/selfPacedApi';
import { AppButton, EmptyState, ConfirmModal } from '../ui';
import ModuleCard from './ModuleCard';
import AddModuleForm from './AddModuleForm';
import AddLessonModal from './AddLessonModal';
import LessonPreviewModal from './LessonPreviewModal';
import './_self-paced-pages.scss';

const CurriculumBuilder = ({ courseId, course, modules = [], onRefresh, setToast }) => {
  const [sequential, setSequential] = useState(course?.sequentialModules || false);

  const handleToggleSequential = async (checked) => {
    setSequential(checked);
    try {
      await updateSelfPacedCourse(courseId, { sequentialModules: checked });
      setToast({ show: true, message: checked ? 'Sequential progression enabled.' : 'Sequential progression disabled.', type: 'success' });
    } catch {
      setSequential(!checked);
      setToast({ show: true, message: 'Failed to update progression mode.', type: 'error' });
    }
  };
  const [addingModule, setAddingModule] = useState(false);
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [moduleSaving, setModuleSaving] = useState(false);
  const [lessonModal, setLessonModal] = useState({
    show: false,
    moduleId: null,
    lesson: null,
  });
  const [expandedModules, setExpandedModules] = useState({});

  /* ── Module CRUD ─────────────────────────────────────────── */

  const handleAddModule = async (body) => {
    setModuleSaving(true);
    try {
      await createModule({ ...body, courseId });
      setToast({ show: true, message: 'Module created.', type: 'success' });
      setAddingModule(false);
      onRefresh();
    } catch (err) {
      setToast({
        show: true,
        message: err?.response?.data?.message || 'Failed to create module.',
        type: 'error',
      });
    } finally {
      setModuleSaving(false);
    }
  };

  const handleUpdateModule = async (body) => {
    setModuleSaving(true);
    try {
      await updateModule(editingModuleId, body);
      setToast({ show: true, message: 'Module updated.', type: 'success' });
      setEditingModuleId(null);
      onRefresh();
    } catch (err) {
      setToast({
        show: true,
        message: err?.response?.data?.message || 'Failed to update module.',
        type: 'error',
      });
    } finally {
      setModuleSaving(false);
    }
  };

  const [deleteModuleId, setDeleteModuleId] = useState(null);
  const [deleteLessonId, setDeleteLessonId] = useState(null);

  const handleDeleteModule = async (id) => {
    setDeleteModuleId(null);
    try {
      await deleteModule(id);
      setToast({ show: true, message: 'Module deleted.', type: 'success' });
      onRefresh();
    } catch (err) {
      setToast({
        show: true,
        message: err?.response?.data?.message || 'Failed to delete module.',
        type: 'error',
      });
    }
  };

  /* ── Lesson helpers ──────────────────────────────────────── */

  const handleDeleteLesson = async (lessonId) => {
    setDeleteLessonId(null);
    try {
      await deleteLesson(lessonId);
      setToast({ show: true, message: 'Lesson deleted.', type: 'success' });
      onRefresh();
    } catch (err) {
      setToast({
        show: true,
        message: err?.response?.data?.message || 'Failed to delete lesson.',
        type: 'error',
      });
    }
  };

  const handleConfirmUpload = async (lessonId) => {
    try {
      await confirmLessonUpload(lessonId);
      setToast({
        show: true,
        message: 'Upload confirmed — lesson is now active.',
        type: 'success',
      });
      onRefresh();
    } catch (err) {
      setToast({
        show: true,
        message: err?.response?.data?.message || 'Confirm failed.',
        type: 'error',
      });
    }
  };

  const toggleModule = (id) =>
    setExpandedModules((p) => ({ ...p, [id]: !p[id] }));

  const openLessonModal = (moduleId, lesson = null) =>
    setLessonModal({ show: true, moduleId, lesson });

  /* ── Preview lesson state ─────────────────────────────── */
  const [previewLesson, setPreviewLesson] = useState(null);

  /* ── AI Explanation state ────────────────────────────── */
  const [aiTriggeringId, setAiTriggeringId] = useState(null);

  // Fetch bulk AI status and merge into modules on mount/refresh
  const fetchAIStatuses = useCallback(async () => {
    const allLessons = modules.flatMap(m => m.lessons || []);
    const aiEligible = allLessons.filter(l =>
      ['pdf', 'ppt', 'docx', 'text'].includes(l.type) && l.status === 'ready'
    );
    if (aiEligible.length === 0) return;
    try {
      const res = await getBulkAIStatus(aiEligible.map(l => l._id));
      const statuses = res.data?.data || res.data || {};
      // Merge aiProcessingStatus into lesson objects
      modules.forEach(mod => {
        (mod.lessons || []).forEach(lesson => {
          if (statuses[lesson._id]) {
            lesson.aiProcessingStatus = statuses[lesson._id].aiProcessingStatus || 'none';
            lesson.aiPagesCount = statuses[lesson._id].aiPagesCount || 0;
          }
        });
      });
    } catch {
      // silently ignore — badges just won't show
    }
  }, [modules]);

  useEffect(() => { fetchAIStatuses(); }, [fetchAIStatuses]);

  const handleTriggerAI = async (lessonId, isRetry = false) => {
    setAiTriggeringId(lessonId);
    try {
      if (isRetry) {
        await retryAIExplanation(lessonId);
      } else {
        await triggerAIExplanation(lessonId);
      }
      setToast({
        show: true,
        message: isRetry ? 'AI explanation regeneration queued.' : 'AI explanation generation queued.',
        type: 'success',
      });
      onRefresh();
    } catch (err) {
      setToast({
        show: true,
        message: err?.response?.data?.message || 'Failed to trigger AI explanation.',
        type: 'error',
      });
    } finally {
      setAiTriggeringId(null);
    }
  };

  const totalLessons = modules.reduce(
    (sum, m) => sum + (m.lessons?.length || 0),
    0,
  );

  /* ── Render ──────────────────────────────────────────────── */

  return (
    <div className="sp-curriculum">      {/* Sequential Progression Toggle */}
      <div className="sp-sequential-toggle">
        <div className="sp-sequential-toggle__info">
          
          <div>
            <span className="sp-sequential-toggle__label"><Lock size={16} /> Sequential Module Progression</span>
            <span className="sp-sequential-toggle__desc">
              {sequential
                ? 'Students must complete each module in order before unlocking the next.'
                : 'All modules are accessible. Enable to lock modules behind prerequisites.'}
            </span>
          </div>
        </div>
        <Form.Check
          type="switch"
          id="sequentialModulesToggle"
          checked={sequential}
          onChange={(e) => handleToggleSequential(e.target.checked)}
          className="sp-sequential-toggle__switch"
        />
      </div>      {/* Empty State */}
      {modules.length === 0 && !addingModule && (
        <EmptyState
          icon={<Layers size={40} strokeWidth={1.5} />}
          title="No Modules Yet"
          description="Start building your course by adding the first module."
          action={
            <AppButton size="sm" onClick={() => setAddingModule(true)}>
              <Plus size={14} /> Add First Module
            </AppButton>
          }
        />
      )}

      {/* Module List */}
      {modules.map((mod, idx) => {
        const isEditing = editingModuleId === mod._id;
        const isExpanded = expandedModules[mod._id] !== false;

        if (isEditing) {
          return (
            <AddModuleForm
              key={mod._id}
              initial={mod}
              onSave={handleUpdateModule}
              onCancel={() => setEditingModuleId(null)}
              saving={moduleSaving}
              sequential={sequential}
            />
          );
        }

        return (
          <ModuleCard
            key={mod._id}
            module={mod}
            index={idx}
            isExpanded={isExpanded}
            sequential={sequential}
            onToggle={() => toggleModule(mod._id)}
            onEdit={() => setEditingModuleId(mod._id)}
            onDelete={() => setDeleteModuleId(mod._id)}
            onAddLesson={() => openLessonModal(mod._id)}
            onEditLesson={(lesson) => openLessonModal(mod._id, lesson)}
            onDeleteLesson={(lessonId) => setDeleteLessonId(lessonId)}
            onConfirmUpload={handleConfirmUpload}
            onPreviewLesson={(lesson) => setPreviewLesson(lesson)}
            onTriggerAI={handleTriggerAI}
            aiTriggeringId={aiTriggeringId}
          />
        );
      })}

      {/* Inline Add Module Form or Dashed Button */}
      {addingModule ? (
        <AddModuleForm
          onSave={handleAddModule}
          onCancel={() => setAddingModule(false)}
          saving={moduleSaving}
          sequential={sequential}
        />
      ) : modules.length > 0 ? (
        <button
          type="button"
          className="sp-add-module-btn"
          onClick={() => setAddingModule(true)}
        >
          <Plus size={16} /> Add New Module
        </button>
      ) : null}

      {/* Lesson Modal */}
      {lessonModal.show && (
        <AddLessonModal
          show={lessonModal.show}
          onHide={() =>
            setLessonModal({ show: false, moduleId: null, lesson: null })
          }
          initial={lessonModal.lesson}
          moduleId={lessonModal.moduleId}
          courseId={courseId}
          onSaved={onRefresh}
          setToast={setToast}
        />
      )}

      {/* Lesson Preview Modal */}
      {previewLesson && (
        <LessonPreviewModal
          show={!!previewLesson}
          onHide={() => setPreviewLesson(null)}
          lesson={previewLesson}
        />
      )}

      {/* Delete Module Confirm */}
      <ConfirmModal
        show={!!deleteModuleId}
        onHide={() => setDeleteModuleId(null)}
        onConfirm={() => handleDeleteModule(deleteModuleId)}
        title="Delete Module"
        message="Delete this module and all its lessons?"
        confirmText="Delete"
        variant="danger"
      />

      {/* Delete Lesson Confirm */}
      <ConfirmModal
        show={!!deleteLessonId}
        onHide={() => setDeleteLessonId(null)}
        onConfirm={() => handleDeleteLesson(deleteLessonId)}
        title="Delete Lesson"
        message="Delete this lesson?"
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

export default CurriculumBuilder;
